import { effortMinutes } from './activity.js'
import { clamp, daysBetween } from './date.js'
import type { Activity } from './types.js'

/**
 * La vitalité de l'avatar (0-100).
 *
 * Modèle : chaque séance apporte de l'énergie, et cette énergie décroît
 * exponentiellement avec le temps. Rien n'est stocké — la vitalité se
 * recalcule toujours depuis le journal d'activités. C'est ce qui rend le
 * comportement "Tamagotchi" déterministe, testable, et impossible à
 * désynchroniser entre l'app, le serveur et le backoffice.
 *
 * Calibrage : quelqu'un qui tient la reco OMS (150 min modérées/semaine,
 * réparties en 3 séances) se stabilise autour de 80. Quelqu'un qui s'arrête
 * perd la moitié de sa vitalité en 3 jours et demi.
 */
export const VITALITY_HALF_LIFE_DAYS = 3.5
export const VITALITY_PER_EFFORT_MINUTE = 0.6
export const VITALITY_MAX = 100

export type VitalityLevel = 'exhausted' | 'low' | 'ok' | 'good' | 'peak'

function decayFactor(daysAgo: number): number {
  return Math.pow(0.5, daysAgo / VITALITY_HALF_LIFE_DAYS)
}

export function computeVitality(
  activities: readonly Activity[],
  now: Date = new Date(),
): number {
  let total = 0
  for (const activity of activities) {
    const daysAgo = daysBetween(new Date(activity.startedAt), now)
    if (daysAgo < 0) continue // séance future : ignorée
    total += effortMinutes(activity) * VITALITY_PER_EFFORT_MINUTE * decayFactor(daysAgo)
  }
  return Math.round(clamp(total, 0, VITALITY_MAX))
}

export function vitalityLevel(vitality: number): VitalityLevel {
  if (vitality < 15) return 'exhausted'
  if (vitality < 35) return 'low'
  if (vitality < 60) return 'ok'
  if (vitality < 85) return 'good'
  return 'peak'
}

/** Vitalité projetée dans `days` jours si l'utilisateur ne bouge pas. */
export function projectVitality(vitality: number, days: number): number {
  return Math.round(vitality * decayFactor(days))
}

/**
 * Dans combien de jours la vitalité passera-t-elle sous `threshold` sans
 * activité ? Sert à programmer la notification "ton avatar s'essouffle".
 * `null` si le seuil est déjà franchi.
 */
export function daysUntilVitalityBelow(
  vitality: number,
  threshold: number,
): number | null {
  if (vitality <= threshold) return null
  if (threshold <= 0) return null
  return (
    Math.log(threshold / vitality) / Math.log(0.5) * VITALITY_HALF_LIFE_DAYS
  )
}

/** Gain de vitalité qu'apporterait une séance, à afficher avant de valider. */
export function vitalityGain(activity: Activity): number {
  return Math.round(effortMinutes(activity) * VITALITY_PER_EFFORT_MINUTE)
}
